// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SetProjectIdAction } from 'Features/project/projectActions';

// Which input files are selected (persistent highlight; AUT-5131 reads this for
// bulk download) and which one is hovered, keyed by the file's Mongo _id.
// Selection is independent of raster visibility: hiding a tile never deselects it.
const initialState: IFileSelectionState = {
  selected: { tif: [], las: [], pano: [], poleImage: [] },
  anchor: { tif: null, las: null, pano: null, poleImage: null },
  hovered: null,
  flash: null,
};

const fileSelectionSlice = createSlice({
  name: 'fileSelection',
  initialState,
  reducers: {
    toggleSelected: (state, action: PayloadAction<{ kind: FileSelectionKind; id: string }>) => {
      const { kind, id } = action.payload;
      const ids = state.selected[kind];
      state.selected[kind] = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
      state.anchor[kind] = id;
    },
    // Shift-click ranges follow the checkbox-list convention (D59): the
    // clicked row's own toggle direction applies to the whole anchor..clicked
    // run - selectRange when it was unselected, deselectRange when selected.
    // Neither moves the anchor: it is always the last PLAIN-clicked row, so
    // repeated shift-clicks re-range from the same spot.
    selectRange: (state, action: PayloadAction<{ kind: FileSelectionKind; ids: string[] }>) => {
      const { kind, ids } = action.payload;
      state.selected[kind] = Array.from(new Set([...state.selected[kind], ...ids]));
    },
    deselectRange: (state, action: PayloadAction<{ kind: FileSelectionKind; ids: string[] }>) => {
      const { kind, ids } = action.payload;
      state.selected[kind] = state.selected[kind].filter((id) => !ids.includes(id));
    },
    // Entering a draw/measure tool wipes the whole selection (D51): its fade
    // and highlights are clutter under an active tool.
    clearAllSelected: (state) => {
      state.selected = { tif: [], las: [], pano: [], poleImage: [] };
      state.anchor = { tif: null, las: null, pano: null, poleImage: null };
    },
    setHovered: (state, action: PayloadAction<IHoveredFiles>) => {
      state.hovered = action.payload;
    },
    clearHovered: (state) => {
      state.hovered = null;
    },
    setFlash: (state, action: PayloadAction<IFileFlash>) => {
      state.flash = action.payload;
    },
    clearFlash: (state) => {
      state.flash = null;
    },
  },
  extraReducers: (builder) => {
    // Selection and hover never leak across projects: every page that shows
    // these files dispatches SetProjectIdAction on mount and null on unmount.
    builder.addCase(SetProjectIdAction, () => initialState);
  },
});

export const {
  toggleSelected, clearAllSelected, selectRange, deselectRange,
  setHovered, clearHovered, setFlash, clearFlash,
} = fileSelectionSlice.actions;

export const { reducer } = fileSelectionSlice;

export default reducer;
